(() => {
  const desktopMedia = window.matchMedia('(min-width: 1001px)');
  const optionAliases = {
    color: ['color', 'colour'],
    size: ['size'],
    helmetType: ['helmet type', 'model', 'select a bundle']
  };
  const state = {
    mounted: false,
    mainSection: null,
    root: null,
    product: null,
    wrapper: null,
    buyButtons: null,
    policyBlock: null,
    policyPlaceholder: null,
    fitGuidePopup: null,
    fitGuidePlaceholder: null,
    availability: null,
    variantScrollPosition: null,
    variantScrollTimer: null
  };

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function getLayoutSettings(root) {
    const dataset = root?.dataset || {};

    return {
      gallerySide: dataset.desktopGallerySide === 'right' ? 'right' : 'left',
      buyButtonsPosition: dataset.buyButtonsPosition === 'block' ? 'block' : 'sticky',
      policyPosition: dataset.policyPosition === 'block' ? 'block' : 'gallery'
    };
  }

  function findOptionPosition(optionNames, aliases) {
    const index = optionNames.findIndex((name) => aliases.includes(normalize(name)));
    return index === -1 ? null : index + 1;
  }

  function formatMoney(cents) {
    if (window.Currency?.formatMoney) {
      return window.Currency.formatMoney(cents, window.theme?.moneyFormat);
    }

    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  class UltraVariantAvailability {
    constructor(root) {
      this.root = root;
      this.shell = root.closest('[data-ultra-pdp-shell]');
      this.fixedSize = this.shell?.dataset.ultraFixedSize === 'true';
      this.fixedVersion = this.shell?.dataset.ultraFixedVersion === 'true';
      this.productHandle = root.querySelector('.global-product-handle')?.value || '';
      this.form = root.querySelector('.ProductForm');
      this.productJson = this.form?.querySelector('[data-product-json]');
      this.createdLabels = [];
      this.serverPriceStates = new Map();
      this.sizePlaceholder = null;
      this.missingOptionElement = null;
      this.purchaseSelectionRequired = false;
      this.syncFrame = null;
      this.missingOptionTimer = null;
      this.ctaObserver = null;
      this.hiddenVariant = null;
      this.lastSyncedVariantId = null;
      this.variantControls = [];
      this.handleChange = this.handleChange.bind(this);
      this.handleCtaClick = this.handleCtaClick.bind(this);
      this.handleLoad = this.handleLoad.bind(this);
      this.handleCtaMutation = this.handleCtaMutation.bind(this);

      if (!this.form || !this.productJson) return;

      try {
        this.product = JSON.parse(this.productJson.textContent).product;
      } catch {
        return;
      }

      if (!this.product?.variants?.length || !this.product?.options?.length) return;

      const optionNames = this.product.options.map((option) => (
        typeof option === 'string' ? option : option.name
      ));

      this.positions = {
        color: findOptionPosition(optionNames, optionAliases.color),
        size: findOptionPosition(optionNames, optionAliases.size),
        helmetType: findOptionPosition(optionNames, optionAliases.helmetType)
      };

      if (Object.values(this.positions).some((position) => position === null)) return;

      this.availableVariants = this.product.variants.filter((variant) => variant.available);
      const allOptionInputs = Array.from(
        this.form.querySelectorAll('.ProductForm__Option input[type="radio"][data-option-position]')
      );
      this.optionInputs = allOptionInputs.filter((input) => (
        !input.closest('.ultra-hidden-option')
      ));
      this.hiddenOptionInputs = allOptionInputs.filter((input) => (
        Boolean(input.closest('.ultra-hidden-option'))
      ));
      this.bundleInputs = Array.from(
        this.form.querySelectorAll(
          '.ProductForm__Option.version .extra-bundle input[type="radio"][data-variant="Helmet Type"]'
        )
      );
      this.managedInputs = Array.from(new Set([
        ...this.optionInputs,
        ...this.hiddenOptionInputs,
        ...this.bundleInputs
      ]));
      this.variantControls = Array.from(
        this.form.querySelectorAll('select[name="id"], input[name="id"]')
      );
      this.initialInputStates = this.managedInputs.map((input) => {
        const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        return {
          input,
          disabled: input.disabled,
          inputAriaDisabled: input.getAttribute('aria-disabled'),
          label,
          labelAriaDisabled: label?.getAttribute('aria-disabled') || null
        };
      });

      this.decorateOptions();
      this.reorderOptions();
      this.form.addEventListener('change', this.handleChange);
      this.form.addEventListener('click', this.handleCtaClick, true);
      window.addEventListener('load', this.handleLoad, { once: true });
      this.observeCta();
      this.sync();
      this.root.dataset.ultraAvailabilityReady = 'true';
    }

    inputsAt(position) {
      return this.optionInputs.filter(
        (input) => Number(input.dataset.optionPosition) === position
      );
    }

    optionElement(position) {
      return this.inputsAt(position)[0]?.closest('.ProductForm__Option') || null;
    }

    selectedValue(position) {
      const visibleValue = this.inputsAt(position).find(
        (input) => input.checked && !input.disabled
      )?.value;
      if (visibleValue) return visibleValue;

      return this.hiddenInputsAt(position).find(
        (input) => input.checked && !input.disabled
      )?.value || null;
    }

    hiddenInputsAt(position) {
      return this.hiddenOptionInputs.filter(
        (input) => Number(input.dataset.optionPosition) === position
      );
    }

    visibleSelections() {
      const selections = new Map();

      this.optionInputs.forEach((input) => {
        if (!input.checked || input.disabled) return;
        selections.set(Number(input.dataset.optionPosition), input.value);
      });

      return selections;
    }

    hiddenOptionGroups() {
      const groups = new Map();

      this.hiddenOptionInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const group = input.closest('.ultra-hidden-option');
        if (!group || !position) return;

        if (!groups.has(position)) groups.set(position, { position, inputs: [] });
        groups.get(position).inputs.push(input);
      });

      return [...groups.values()].sort((a, b) => a.position - b.position);
    }

    variantOptionValue(variant, position) {
      return variant?.options?.[position - 1] ?? variant?.[`option${position}`];
    }

    syncHiddenOptions() {
      const groups = this.hiddenOptionGroups();
      this.hiddenVariant = null;
      if (!groups.length) return;

      const visibleSelections = this.visibleSelections();
      const candidates = this.availableVariants.filter((variant) => (
        [...visibleSelections].every(([position, value]) => (
          this.variantMatches(variant, position, value)
        )) && groups.every(({ position }) => (
          this.variantOptionValue(variant, position) !== undefined
        ))
      ));
      if (!candidates.length) return;

      const currentHiddenValues = new Map(
        groups.map(({ position, inputs }) => [
          position,
          inputs.find((input) => input.checked)?.value || null
        ])
      );
      const candidate = candidates.reduce((best, variant) => {
        if (!best) return variant;

        const score = groups.reduce((total, { position }) => (
          total + (this.variantOptionValue(variant, position) === currentHiddenValues.get(position) ? 1 : 0)
        ), 0);
        const bestScore = groups.reduce((total, { position }) => (
          total + (this.variantOptionValue(best, position) === currentHiddenValues.get(position) ? 1 : 0)
        ), 0);

        return score > bestScore ? variant : best;
      }, null);

      let hiddenSelectionChanged = false;
      let changedHiddenInput = null;
      groups.forEach(({ position, inputs }) => {
        const value = this.variantOptionValue(candidate, position);
        const selectedInput = inputs.find((input) => input.value === value);
        if (!selectedInput) return;

        inputs.forEach((input) => {
          const checked = input === selectedInput;
          if (input.checked !== checked) {
            input.checked = checked;
            hiddenSelectionChanged = true;
            if (checked) changedHiddenInput = input;
          }
        });
      });

      const selections = this.visibleSelections();
      groups.forEach(({ position, inputs }) => {
        const selected = inputs.find((input) => input.checked && !input.disabled);
        if (selected) selections.set(position, selected.value);
      });
      const complete = this.product.options.every((_, index) => selections.has(index + 1));
      if (!complete) return;

      const actualVariant = this.availableVariants.find((variant) => (
        [...selections].every(([position, value]) => (
          this.variantMatches(variant, position, value)
        ))
      ));
      if (!actualVariant) return;

      this.hiddenVariant = actualVariant;
      if (this.hiddenInputsAt(this.positions.helmetType).length) {
        this.purchaseSelectionRequired = false;
      }
      if (hiddenSelectionChanged && changedHiddenInput) {
        const event = new Event('change', { bubbles: true });
        event.ultraHiddenVariantSync = true;
        changedHiddenInput.dispatchEvent(event);
      }
      this.syncHostVariant(actualVariant);
    }

    syncHostVariant(variant) {
      const variantId = String(variant?.id ?? '');
      const control = this.variantControls?.[0];
      if (!variantId || !control) return;
      if (control.value === variantId) {
        this.lastSyncedVariantId = variantId;
        return;
      }
      if (this.lastSyncedVariantId === variantId) return;

      control.value = variantId;
      this.lastSyncedVariantId = variantId;
    }

    ensureFixedSizeSelection() {
      if (!this.fixedSize) return;

      const sizeInputs = this.inputsAt(this.positions.size);
      if (sizeInputs.length !== 1) return;

      const input = sizeInputs[0];
      if (!input.disabled && !input.checked) input.checked = true;
    }

    ensureFixedVersionSelection() {
      if (!this.fixedVersion) return;

      const inputs = this.purchaseInputs();
      if (inputs.length !== 1) return;

      inputs[0].checked = true;
      this.purchaseSelectionRequired = false;
    }

    purchaseInputs() {
      return [...this.inputsAt(this.positions.helmetType), ...this.bundleInputs];
    }

    selectedPurchaseInput() {
      if (this.purchaseSelectionRequired && !this.fixedVersion) return null;

      if (this.hiddenInputsAt(this.positions.helmetType).length) {
        return this.hiddenVariant
          ? this.hiddenInputsAt(this.positions.helmetType).find((input) => input.checked) || null
          : null;
      }

      return this.purchaseInputs().find((input) => (
        input.checked && (this.fixedVersion || !input.disabled)
      )) || null;
    }

    requirePurchaseSelection() {
      this.purchaseSelectionRequired = true;
    }

    decorateOptions() {
      const soldOutLabel = this.shell?.dataset.ultraSoldOutLabel || 'Sold out';

      this.managedInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        if (!label || position === this.positions.color) return;
        if (label.querySelector('.UltraOptionAvailabilityLabel')) return;

        const status = document.createElement('span');
        status.className = 'UltraOptionAvailabilityLabel';
        status.textContent = soldOutLabel;
        label.appendChild(status);
        this.createdLabels.push(status);
      });
    }

    reorderOptions() {
      const sizeOption = this.optionElement(this.positions.size);
      const helmetTypeOption = this.optionElement(this.positions.helmetType);

      if (!sizeOption || !helmetTypeOption || sizeOption.parentNode !== helmetTypeOption.parentNode) return;
      if (sizeOption.nextElementSibling === helmetTypeOption) return;

      this.sizeOption = sizeOption;
      this.sizePlaceholder = document.createComment('ultra-size-option');
      sizeOption.parentNode.insertBefore(this.sizePlaceholder, sizeOption);
      helmetTypeOption.parentNode.insertBefore(sizeOption, helmetTypeOption);
    }

    variantMatches(variant, position, value) {
      return this.variantOptionValue(variant, position) === value;
    }

    hasAvailableVariant(position, value, fixedSelections = []) {
      return this.availableVariants.some((variant) => {
        if (!this.variantMatches(variant, position, value)) return false;

        return fixedSelections.every(([fixedPosition, fixedValue]) => (
          this.variantMatches(variant, fixedPosition, fixedValue)
        ));
      });
    }

    variantForSelections(position, value, fixedSelections = []) {
      return this.product.variants.find((variant) => {
        if (!this.variantMatches(variant, position, value)) return false;

        return fixedSelections.every(([fixedPosition, fixedValue]) => (
          this.variantMatches(variant, fixedPosition, fixedValue)
        ));
      });
    }

    sizeKey(value) {
      return String(value || '').trim().split(/\s+/)[0].replace('/', '-');
    }

    isWinterBundle(input) {
      return normalize(input.value).includes('winter kit');
    }

    winterLinerSize(size) {
      const normalizedSize = this.sizeKey(size).toUpperCase();
      if (this.productHandle !== 'lumos-nyxel') return normalizedSize;

      return {
        S: 'S',
        M: 'M-L',
        L: 'XL'
      }[normalizedSize] || normalizedSize;
    }

    bundleOptions(input) {
      return Array.from(
        input.closest('.extra-bundle')?.querySelectorAll('.hidden-selector option') || []
      );
    }

    bundleOptionMatches(input, option, color, size) {
      if (!color || !size) return false;
      if (option.dataset.option1 !== color) return false;
      if (this.sizeKey(option.dataset.option2) !== this.sizeKey(size)) return false;

      return !this.isWinterBundle(input) ||
        this.sizeKey(option.dataset.option3).toUpperCase() === this.winterLinerSize(size);
    }

    hasAvailableBundle(input, color, size) {
      return this.bundleOptions(input).some((option) => (
        !option.disabled && this.bundleOptionMatches(input, option, color, size)
      ));
    }

    hasAvailableBundleColor(input, color) {
      return this.bundleOptions(input).some((option) => {
        if (option.disabled || option.dataset.option1 !== color) return false;

        return !this.isWinterBundle(input) ||
          this.sizeKey(option.dataset.option3).toUpperCase() ===
            this.winterLinerSize(option.dataset.option2);
      });
    }

    setInputAvailability(input, isAvailable, isAwaitingSelection = false) {
      const item = input.closest('.HorizontalList__Item');
      const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const isDisabled = isAwaitingSelection || !isAvailable;

      input.disabled = isDisabled;
      input.setAttribute('aria-disabled', String(isDisabled));
      item?.classList.toggle('is-ultra-unavailable', !isAwaitingSelection && !isAvailable);
      item?.classList.toggle('is-ultra-awaiting-selection', isAwaitingSelection);

      if (label) label.setAttribute('aria-disabled', String(isDisabled));
    }

    updateColorOptions() {
      this.inputsAt(this.positions.color).forEach((input) => {
        const hasMainVariant = this.hasAvailableVariant(this.positions.color, input.value);
        const hasBundleVariant = this.bundleInputs.some((bundleInput) => (
          this.hasAvailableBundleColor(bundleInput, input.value)
        ));

        this.setInputAvailability(input, hasMainVariant || hasBundleVariant);
      });
    }

    updateSizeOptions(selectedColor) {
      const isAwaitingSelection = !selectedColor;

      this.inputsAt(this.positions.size).forEach((input) => {
        const hasMainVariant = !isAwaitingSelection && this.hasAvailableVariant(
          this.positions.size,
          input.value,
          [[this.positions.color, selectedColor]]
        );
        const hasBundleVariant = !isAwaitingSelection && this.bundleInputs.some((bundleInput) => (
          this.hasAvailableBundle(bundleInput, selectedColor, input.value)
        ));

        this.setInputAvailability(
          input,
          hasMainVariant || hasBundleVariant,
          isAwaitingSelection
        );
      });
    }

    updatePurchaseOptions(selectedColor, selectedSize) {
      const isAwaitingSelection = !selectedColor || !selectedSize;

      this.inputsAt(this.positions.helmetType).forEach((input) => {
        const isAvailable = !isAwaitingSelection && this.hasAvailableVariant(
          this.positions.helmetType,
          input.value,
          [
            [this.positions.color, selectedColor],
            [this.positions.size, selectedSize]
          ]
        );

        this.setInputAvailability(input, isAvailable, isAwaitingSelection);
        this.updateMainPurchasePrice(input, selectedColor, selectedSize);
      });

      this.bundleInputs.forEach((input) => {
        const isAvailable = !isAwaitingSelection &&
          this.hasAvailableBundle(input, selectedColor, selectedSize);

        this.setInputAvailability(input, isAvailable, isAwaitingSelection);
      });
    }

    updateMainPurchasePrice(input, selectedColor, selectedSize) {
      const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const wrapper = label?.querySelector('.model-caption-price-wrapper');

      if (!label || !wrapper) return;

      let priceWrapper = wrapper.querySelector('.UltraMainOptionPrice');

      if (!priceWrapper) {
        priceWrapper = wrapper.querySelector(
          ':scope > .firefly-mini-product-price-wrapper'
        );
        if (priceWrapper) {
          this.serverPriceStates.set(priceWrapper, {
            className: priceWrapper.getAttribute('class'),
            variantId: priceWrapper.getAttribute('data-ultra-variant-id')
          });
          priceWrapper.classList.add('UltraMainOptionPrice');
        }
      }

      if (!selectedColor || !selectedSize) {
        if (priceWrapper && !this.serverPriceStates.has(priceWrapper)) {
          priceWrapper.remove();
        }
        return;
      }

      const variant = this.variantForSelections(
        this.positions.helmetType,
        input.value,
        [
          [this.positions.color, selectedColor],
          [this.positions.size, selectedSize]
        ]
      );

      if (!variant) {
        if (priceWrapper && !this.serverPriceStates.has(priceWrapper)) {
          priceWrapper.remove();
        }
        return;
      }

      if (!priceWrapper) {
        priceWrapper = document.createElement('div');
        priceWrapper.className = 'firefly-mini-product-price-wrapper UltraMainOptionPrice';
        wrapper.appendChild(priceWrapper);
      }

      const compareAtPrice = Number(variant.compare_at_price || 0);
      const price = Number(variant.price || 0);
      const variantId = String(variant.id);

      if (priceWrapper?.dataset.ultraVariantId === variantId) return;

      if (compareAtPrice > price) {
        const save = Math.round((compareAtPrice - price) / 100);
        priceWrapper.innerHTML = `
          <div class="ProductMeta__Price__Wrapper">
            <span class="ProductMeta__Price Price Price--highlight Text--subdued u-h4">${formatMoney(price)}</span>
            <span class="ProductMeta__Price Price Price--compareAt Text--subdued u-h4">${formatMoney(compareAtPrice)}</span>
          </div>
          <div class="firefly-mini-save">Save $${save}</div>
        `;
      } else {
        priceWrapper.innerHTML = `<span class="model-price">${formatMoney(price)}</span>`;
      }
      priceWrapper.dataset.ultraVariantId = variantId;
    }

    clearIfUnavailable(position) {
      const selectedInput = this.inputsAt(position).find((input) => input.checked);

      if (!selectedInput) return false;
      if (!selectedInput.disabled) return false;
      return true;
    }

    clearPurchaseIfUnavailable() {
      const selectedInput = this.purchaseInputs().find((input) => input.checked);

      if (!selectedInput?.disabled) return false;
      if (this.fixedVersion) return false;
      this.requirePurchaseSelection();
      return true;
    }

    resetBundlePresentation() {
      const variants = this.form.querySelector('.ProductForm__Variants');
      const priceList = this.root.querySelector('.ProductMeta__PriceList');
      const masterSelect = this.form.querySelector('.no-js.ProductForm__Option.main-product select');

      variants?.classList.remove('has-bundle-product');
      priceList?.classList.remove('has-bundle-product');

      masterSelect?.querySelectorAll('option[data-value-original]').forEach((option) => {
        option.value = option.dataset.valueOriginal;
      });
    }

    optionScrollParent() {
      return this.root.querySelector('.Product__Info') || this.form;
    }

    setMissingOption(element) {
      if (this.missingOptionElement && this.missingOptionElement !== element) {
        this.missingOptionElement.classList.remove('is-ultra-option-needed');
      }

      this.missingOptionElement = element || null;
    }

    scrollToMissingOption() {
      const target = this.missingOptionElement;

      if (!target) return;

      const scroller = this.optionScrollParent();
      const scrollerRect = scroller.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = scroller.scrollTop + targetRect.top - scrollerRect.top;
      const alignedTop = Math.max(0, targetTop - 12);

      scroller.scrollTo({ top: alignedTop, behavior: 'smooth' });
      target.classList.add('is-ultra-option-needed');
      window.clearTimeout(this.missingOptionTimer);
      this.missingOptionTimer = window.setTimeout(() => {
        target.classList.remove('is-ultra-option-needed');
      }, 1100);
    }

    setIncompleteState(prompt, optionElement, promptClass) {
      const button = this.form.querySelector('.ProductForm__AddToCart');
      const paymentButton = this.form.querySelector('.shopify-payment-button');
      const stateElements = [this.root, this.shell].filter(Boolean);

      stateElements.forEach((element) => {
        element.classList.add('is-ultra-selection-incomplete');
        element.classList.remove(
          'is-ultra-needs-color',
          'is-ultra-needs-size',
          'is-ultra-needs-version'
        );
        element.classList.add(promptClass);
      });
      this.setMissingOption(optionElement);
      if (button) {
        let promptButton = this.form.querySelector('.UltraCtaPromptButton');

        if (!promptButton) {
          promptButton = document.createElement('button');
          promptButton.type = 'button';
          promptButton.className = [
            ...button.classList
          ].filter((className) => className !== 'ProductForm__AddToCart').join(' ');
          promptButton.classList.add('UltraCtaPromptButton');
          button.insertAdjacentElement('afterend', promptButton);
        }

        promptButton.textContent = prompt;
        promptButton.setAttribute('aria-label', prompt);
      }
      if (paymentButton) paymentButton.style.display = 'none';
      window.LumosCartOperations?.syncProductForm?.(this.form);
    }

    clearIncompleteState() {
      const button = this.form.querySelector('.ProductForm__AddToCart');
      const paymentButton = this.form.querySelector('.shopify-payment-button');
      const stateElements = [this.root, this.shell].filter(Boolean);

      stateElements.forEach((element) => {
        element.classList.remove(
          'is-ultra-selection-incomplete',
          'is-ultra-needs-color',
          'is-ultra-needs-size',
          'is-ultra-needs-version'
        );
      });
      this.setMissingOption(null);
      this.form.querySelector('.UltraCtaPromptButton')?.remove();
      if (button) {
        button.setAttribute('data-action', 'add-to-cart');
      }

      // Availability settles after ProductVariants; recompute through the request lock owner.
      window.LumosCartOperations?.syncProductForm?.(this.form);
      if (button && !button.disabled) {
        button.removeAttribute('aria-disabled');
        paymentButton?.style.removeProperty('display');
      }
    }

    sync() {
      const { color, size } = this.positions;

      this.syncHiddenOptions();
      this.updateColorOptions();
      if (this.clearIfUnavailable(color)) {
        this.requirePurchaseSelection();
      }

      const selectedColor = this.selectedValue(color);
      this.updateSizeOptions(selectedColor);
      this.ensureFixedSizeSelection();
      if (!this.fixedSize && this.clearIfUnavailable(size)) {
        this.requirePurchaseSelection();
      }

      const selectedSize = this.selectedValue(size);
      this.updatePurchaseOptions(selectedColor, selectedSize);
      this.ensureFixedVersionSelection();
      this.clearPurchaseIfUnavailable();

      const selectedPurchase = this.selectedPurchaseInput();
      const selectedBundle = selectedPurchase && this.bundleInputs.includes(selectedPurchase);

      if (!selectedBundle) this.resetBundlePresentation();

      if (!selectedColor) {
        this.setIncompleteState(
          this.shell?.dataset.ultraChooseColorLabel || 'Choose color',
          this.optionElement(color),
          'is-ultra-needs-color'
        );
      } else if (!selectedSize && !this.fixedSize) {
        this.setIncompleteState(
          this.shell?.dataset.ultraChooseSizeLabel || 'Choose size',
          this.optionElement(size),
          'is-ultra-needs-size'
        );
      } else if (!selectedPurchase && !this.fixedVersion) {
        this.setIncompleteState(
          this.shell?.dataset.ultraChooseVersionLabel || 'Choose version',
          this.optionElement(this.positions.helmetType),
          'is-ultra-needs-version'
        );
      } else {
        this.clearIncompleteState();
      }
    }

    scheduleSync() {
      if (this.syncFrame !== null) return;

      this.syncFrame = requestAnimationFrame(() => {
        this.syncFrame = null;
        this.sync();
      });
    }

    observeCta() {
      if (!window.MutationObserver) return;

      this.ctaObserver = new MutationObserver(this.handleCtaMutation);
      this.ctaObserver.observe(this.form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });
    }

    handleCtaMutation(mutations) {
      const ctaChanged = mutations.some((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          mutation.target.matches('.ProductForm__AddToCart')
        ) {
          return true;
        }

        if (
          mutation.type === 'childList' &&
          mutation.target instanceof Element &&
          (
            mutation.target.matches('.ProductForm__AddToCart') ||
            mutation.target.closest('.ProductForm__AddToCart')
          )
        ) {
          return true;
        }

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => (
          node instanceof Element &&
          (
            node.matches('.ProductForm__AddToCart') ||
            node.querySelector('.ProductForm__AddToCart')
          )
        ));
      });

      if (ctaChanged) this.scheduleSync();
    }

    handleChange(event) {
      if (!event.target.matches('input[type="radio"]')) return;
      if (!this.managedInputs.includes(event.target)) return;
      if (event.ultraHiddenVariantSync) return;

      const position = Number(event.target.dataset.optionPosition);
      const isPurchaseInput = position === this.positions.helmetType ||
        this.bundleInputs.includes(event.target);
      const isFixedSizeInput = this.fixedSize && position === this.positions.size;
      if (isPurchaseInput && event.isTrusted) {
        this.purchaseSelectionRequired = false;
      }

      if (!isPurchaseInput && !isFixedSizeInput && !this.fixedVersion && event.isTrusted) {
        this.requirePurchaseSelection();
      }

      this.scheduleSync();
    }

    handleCtaClick(event) {
      const promptButton = event.target.closest('.UltraCtaPromptButton');

      if (promptButton && this.form.contains(promptButton)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.scrollToMissingOption();
        return;
      }

      const label = event.target.closest('label[for]');
      const labelInput = label && this.form.querySelector(
        `#${CSS.escape(label.getAttribute('for'))}`
      );

      if (
        labelInput &&
        this.purchaseInputs().includes(labelInput) &&
        !labelInput.disabled &&
        this.purchaseSelectionRequired
      ) {
        this.purchaseSelectionRequired = false;
        this.scheduleSync();
      }

      const button = event.target.closest('.ProductForm__AddToCart');

      if (!button || !this.form.contains(button)) return;
      if (!this.root.classList.contains('is-ultra-selection-incomplete')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.scrollToMissingOption();
    }

    handleLoad() {
      requestAnimationFrame(() => this.sync());
    }

    destroy() {
      this.form?.removeEventListener('change', this.handleChange);
      this.form?.removeEventListener('click', this.handleCtaClick, true);
      window.removeEventListener('load', this.handleLoad);
      this.ctaObserver?.disconnect();
      this.ctaObserver = null;
      this.form?.querySelector('.UltraCtaPromptButton')?.remove();
      if (this.syncFrame !== null) {
        window.cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;
      }
      window.clearTimeout(this.missingOptionTimer);
      [this.root, this.shell].filter(Boolean).forEach((element) => {
        element.classList.remove(
          'is-ultra-selection-incomplete',
          'is-ultra-needs-color',
          'is-ultra-needs-size',
          'is-ultra-needs-version'
        );
      });
      this.missingOptionElement?.classList.remove('is-ultra-option-needed');
      delete this.root.dataset.ultraAvailabilityReady;
      window.LumosCartOperations?.syncProductForm?.(this.form);

      this.initialInputStates?.forEach((state) => {
        state.input.disabled = state.disabled;
        state.input.closest('.HorizontalList__Item')?.classList.remove(
          'is-ultra-unavailable',
          'is-ultra-awaiting-selection'
        );

        if (state.inputAriaDisabled === null) {
          state.input.removeAttribute('aria-disabled');
        } else {
          state.input.setAttribute('aria-disabled', state.inputAriaDisabled);
        }

        if (state.label) {
          if (state.labelAriaDisabled === null) {
            state.label.removeAttribute('aria-disabled');
          } else {
            state.label.setAttribute('aria-disabled', state.labelAriaDisabled);
          }
        }
      });

      this.createdLabels.forEach((label) => label.remove());
      this.hiddenVariant = null;
      this.lastSyncedVariantId = null;
      this.serverPriceStates.forEach((serverState, priceWrapper) => {
        if (serverState.className === null) {
          priceWrapper.removeAttribute('class');
        } else {
          priceWrapper.setAttribute('class', serverState.className);
        }

        if (serverState.variantId === null) {
          priceWrapper.removeAttribute('data-ultra-variant-id');
        } else {
          priceWrapper.setAttribute('data-ultra-variant-id', serverState.variantId);
        }
      });
      this.serverPriceStates.clear();

      if (this.sizeOption && this.sizePlaceholder?.parentNode) {
        this.sizePlaceholder.parentNode.insertBefore(this.sizeOption, this.sizePlaceholder);
        this.sizePlaceholder.remove();
      }
    }
  }

  function getVariantInput(target) {
    if (!(target instanceof Element)) return null;

    const input = target.closest('input[type="radio"][data-variant]');
    if (input instanceof HTMLInputElement) return input;

    const label = target.closest('label[for]');
    const labelledInput = label?.htmlFor ? document.getElementById(label.htmlFor) : null;

    if (!(labelledInput instanceof HTMLInputElement)) return null;
    if (labelledInput.type !== 'radio' || !labelledInput.hasAttribute('data-variant')) return null;

    return labelledInput;
  }

  function enforceVariantScroll() {
    const position = state.variantScrollPosition;
    if (!position || !state.mounted || !desktopMedia.matches) return;
    if (window.scrollX === position.x && window.scrollY === position.y) return;

    window.scrollTo(position.x, position.y);
  }

  function releaseVariantScroll() {
    window.clearTimeout(state.variantScrollTimer);
    state.variantScrollPosition = null;
    state.variantScrollTimer = null;
  }

  function rememberVariantScroll(event) {
    const input = getVariantInput(event.target);
    if (!input || !state.root?.contains(input)) return;

    releaseVariantScroll();
    state.variantScrollPosition = {
      x: window.scrollX,
      y: window.scrollY
    };
    state.variantScrollTimer = window.setTimeout(releaseVariantScroll, 250);
  }

  function restoreVariantScroll(event) {
    const input = getVariantInput(event.target);
    if (!input || !state.root?.contains(input) || !state.variantScrollPosition) return;

    requestAnimationFrame(() => {
      enforceVariantScroll();

      requestAnimationFrame(() => {
        enforceVariantScroll();
        releaseVariantScroll();
      });
    });
  }

  function rootFromScope(scope) {
    if (scope?.matches?.('#section-quick-product-v2')) return scope;
    return scope?.querySelector?.('#section-quick-product-v2') || null;
  }

  function mount(scope = document) {
    if (state.mounted || !desktopMedia.matches) return;

    const scopedRoot = rootFromScope(scope);
    const shell = scopedRoot?.closest?.('[data-ultra-pdp-shell]') ||
      document.querySelector('[data-ultra-pdp-shell]');
    const root = scopedRoot || shell?.querySelector('#section-quick-product-v2');
    const mainSection = root?.closest('.shopify-section');
    const product = root?.querySelector('.Product');
    const wrapper = product?.querySelector('.Product__Wrapper');
    const imageContainer = root?.querySelector('.product-img-container');
    const buyButtons = root?.querySelector('.ProductForm .ProductForm__BuyButtons');
    const policyBlock = root?.querySelector('.bottom-icon-main-wrapper');
    const fitGuidePopup = root?.querySelector('.fitguide-popup.desktop-view');
    const layoutSettings = getLayoutSettings(root);

    if (!shell || !root || !mainSection || !product || !wrapper || !buyButtons) return;
    if (mainSection.parentElement !== shell) return;

    let fitGuidePlaceholder = null;
    if (fitGuidePopup?.parentNode) {
      fitGuidePlaceholder = document.createComment('ultra-fit-guide-popup');
      fitGuidePopup.parentNode.insertBefore(fitGuidePlaceholder, fitGuidePopup);
      fitGuidePopup.classList.add('UltraDesktopFitGuide');
      document.body.appendChild(fitGuidePopup);
    }

    let policyPlaceholder = null;
    let mountedPolicyBlock = null;
    if (layoutSettings.policyPosition === 'gallery' && policyBlock?.parentNode && imageContainer) {
      policyPlaceholder = document.createComment('ultra-policy-block');
      policyBlock.parentNode.insertBefore(policyPlaceholder, policyBlock);
      policyBlock.classList.add('UltraDesktopPdpPolicy');
      imageContainer.appendChild(policyBlock);
      mountedPolicyBlock = policyBlock;
    }

    mainSection.classList.add('UltraDesktopPdpMain');
    root.classList.add('UltraDesktopPdpRoot');
    product.classList.add('UltraDesktopPdpProduct');
    wrapper.classList.add('UltraDesktopPdpWrapper');
    buyButtons.classList.add('UltraDesktopPdpCta');
    document.body.classList.add('UltraDesktopPdpActive');

    const availability = new UltraVariantAvailability(root);

    root.addEventListener('pointerdown', rememberVariantScroll, true);
    root.addEventListener('keydown', rememberVariantScroll, true);
    root.addEventListener('change', restoreVariantScroll, true);

    Object.assign(state, {
      mounted: true,
      mainSection,
      root,
      product,
      wrapper,
      buyButtons,
      policyBlock: mountedPolicyBlock,
      policyPlaceholder,
      fitGuidePopup,
      fitGuidePlaceholder,
      availability
    });
  }

  function unmount() {
    if (!state.mounted) return;

    releaseVariantScroll();
    state.root?.removeEventListener('pointerdown', rememberVariantScroll, true);
    state.root?.removeEventListener('keydown', rememberVariantScroll, true);
    state.root?.removeEventListener('change', restoreVariantScroll, true);
    state.availability?.destroy();

    if (state.policyBlock) {
      state.policyBlock.classList.remove('UltraDesktopPdpPolicy');

      if (state.policyPlaceholder?.parentNode) {
        state.policyPlaceholder.parentNode.insertBefore(
          state.policyBlock,
          state.policyPlaceholder
        );
        state.policyPlaceholder.remove();
      }
    }

    if (state.fitGuidePopup) {
      state.fitGuidePopup.classList.remove('active', 'scrollable', 'UltraDesktopFitGuide');
      document.body.classList.remove('popup-open');

      if (state.fitGuidePlaceholder?.parentNode) {
        state.fitGuidePlaceholder.parentNode.insertBefore(state.fitGuidePopup, state.fitGuidePlaceholder);
        state.fitGuidePlaceholder.remove();
      } else if (state.fitGuidePopup.parentNode === document.body) {
        state.fitGuidePopup.remove();
      }
    }

    state.buyButtons?.classList.remove('UltraDesktopPdpCta');
    document.body.classList.remove('UltraDesktopPdpActive');
    state.wrapper?.classList.remove('UltraDesktopPdpWrapper');
    state.product?.classList.remove('UltraDesktopPdpProduct');
    state.root?.classList.remove('UltraDesktopPdpRoot');
    state.mainSection?.classList.remove('UltraDesktopPdpMain');

    Object.keys(state).forEach((key) => {
      state[key] = key === 'mounted' ? false : null;
    });
  }

  function syncLayout(scope = document) {
    if (desktopMedia.matches) {
      mount(scope);
    } else {
      unmount();
    }
  }

  function handleSectionLoad(event) {
    const nextRoot = rootFromScope(event.target);
    if (!nextRoot) return;

    if (state.mounted && state.root !== nextRoot) unmount();
    syncLayout(nextRoot);
  }

  function handleSectionUnload(event) {
    const unloadedRoot = rootFromScope(event.target);
    if (!unloadedRoot) return;

    if (state.root === unloadedRoot || event.target?.contains?.(state.root)) {
      unmount();
    }
  }

  syncLayout();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncLayout, { once: true });
  }

  desktopMedia.addEventListener('change', syncLayout);
  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
})();
